
import { ReactSortable } from "react-sortablejs";
import { useMemo, useState, useEffect } from "react";
import { LayoutItem, sizeToGridClass } from "./utils";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useComponentMap } from "./widgets/ComponentMap";
import { Button } from "@/components/ui/button";
import { PlusCircle, LayoutGrid, Move } from "lucide-react";
import { Link } from "react-router-dom";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

interface DashboardWidgetsProps {
  widgetItems: LayoutItem[];
  handlesVisible: boolean;
  onRemove: (id: string) => void;
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
  onReorder: (newOrder: LayoutItem[]) => void;
  onUpdatePosition: (id: string, position: { x: number, y: number }) => void;
  userStats: any;
  contractsTimeline: any[];
  contractsDistribution: any[];
  recentContracts: any[];
  upcomingExpirations: any[];
  hasData: boolean;
  selectedYear: number;
  layoutType: 'grid' | 'freeform';
  onLayoutTypeChange: (type: 'grid' | 'freeform') => void;
}

interface DashboardItem extends LayoutItem {
  component: JSX.Element;
}

export function DashboardWidgets({
  widgetItems,
  handlesVisible,
  onRemove,
  onResize,
  onReorder,
  onUpdatePosition,
  userStats,
  contractsTimeline,
  contractsDistribution,
  recentContracts,
  upcomingExpirations,
  hasData,
  selectedYear,
  layoutType,
  onLayoutTypeChange
}: DashboardWidgetsProps) {
  const { t } = useEnhancedTranslation();
  const { useUserPreferencesQuery } = useUserPreferences();
  const { data: userPreferences } = useUserPreferencesQuery();
  const { useCurrenciesQuery } = useCurrencies();
  const { data: currencies } = useCurrenciesQuery();
  
  const [defaultCurrency, setDefaultCurrency] = useState<string | null>(null);
  const [defaultCurrencyId, setDefaultCurrencyId] = useState<string | null>(null);
  
  useEffect(() => {
    if (!userPreferences || !currencies) return;
    
    const defaultCurrencyId = userPreferences.defaultCurrencyId;
    if (!defaultCurrencyId) return;
    
    const currency = currencies.find(c => c.id === defaultCurrencyId);
    if (currency) {
      setDefaultCurrency(currency.symbol);
      setDefaultCurrencyId(currency.id);
    }
  }, [userPreferences, currencies]);
  
  const componentMap = useComponentMap(
    userStats,
    contractsTimeline,
    contractsDistribution,
    recentContracts,
    upcomingExpirations,
    handlesVisible,
    onRemove,
    onResize,
    defaultCurrency,
    defaultCurrencyId,
    selectedYear
  );
  
  const dashboardItems = useMemo(() => 
    widgetItems
      .filter(item => componentMap[item.id])
      .map(item => ({
        ...item,
        component: componentMap[item.id](item)
      })),
    [widgetItems, componentMap]
  );

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
        <h3 className="text-xl font-medium text-gray-700 mb-3">{t('dashboard.widgets.noData', 'No contract data yet')}</h3>
        <p className="text-gray-500 mb-6 max-w-md">
          {t('dashboard.widgets.populate', 'Your dashboard will populate with insights once you add contracts to the system.')}
        </p>
        <Button asChild>
          <Link to="/contracts/new">
            <PlusCircle className="mr-2 h-4 w-4" />
            {t('dashboard.widgets.addFirst', 'Add your first contract')}
          </Link>
        </Button>
      </div>
    );
  }

  const toggleLayoutType = () => {
    onLayoutTypeChange(layoutType === 'grid' ? 'freeform' : 'grid');
  };

  const renderLayoutToggle = () => (
    <div className="mb-4 flex justify-end">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={toggleLayoutType}
        className="text-xs flex items-center gap-1.5"
      >
        {layoutType === 'grid' ? (
          <>
            <Move className="h-4 w-4" />
            <span>{t('dashboard.controls.freePositioning', 'Free Positioning')}</span>
          </>
        ) : (
          <>
            <LayoutGrid className="h-4 w-4" />
            <span>{t('dashboard.controls.gridLayout', 'Grid Layout')}</span>
          </>
        )}
      </Button>
    </div>
  );

  if (layoutType === 'grid') {
    return (
      <div className="w-full">
        {handlesVisible && renderLayoutToggle()}
        <ReactSortable
          list={dashboardItems}
          setList={(newState) => {
            console.log("ReactSortable for dashboard items called with:", newState);
            const updatedItems = [...newState];
            onReorder(updatedItems);
          }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full"
          animation={200}
          handle=".cursor-grab"
          ghostClass="opacity-50"
          disabled={!handlesVisible}
          group="widgets"
        >
          {dashboardItems.map(item => (
            <div 
              key={item.id} 
              className={`${sizeToGridClass[item.size] || 'col-span-1'}`}
            >
              {item.component}
            </div>
          ))}
        </ReactSortable>
      </div>
    );
  }

  return (
    <div className="w-full">
      {handlesVisible && renderLayoutToggle()}
      <div className="relative min-h-[800px] border border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
        {dashboardItems.map(item => {
          const position = item.position || { x: 0, y: 0 };
          const widthClass = item.size === 'small' ? 'w-[calc(25%-16px)]' : 
                           item.size === 'medium' ? 'w-[calc(50%-16px)]' : 
                           'w-[calc(100%-16px)]';
          
          return (
            <div 
              key={item.id}
              className={`absolute ${widthClass} transition-all duration-200 ease-in-out`}
              style={{ 
                left: `${position.x * 25}%`, 
                top: `${position.y * 120}px`,
              }}
            >
              <div className={`${handlesVisible ? 'cursor-move' : ''} h-full`}>
                {item.component}
                {handlesVisible && (
                  <div className="absolute top-0 left-0 w-full h-full bg-transparent group">
                    <div 
                      className="absolute top-2 left-2 bg-primary text-white p-1 rounded opacity-0 group-hover:opacity-80"
                      onMouseDown={(e) => {
                        if (!handlesVisible) return;
                        
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startLeft = position.x;
                        const startTop = position.y;
                        
                        const onMouseMove = (moveEvent: MouseEvent) => {
                          const deltaX = (moveEvent.clientX - startX) / (document.querySelector('.dashboard-container')?.clientWidth || 1000) * 4;
                          const deltaY = (moveEvent.clientY - startY) / 120;
                          
                          const newX = Math.max(0, Math.min(3, startLeft + deltaX));
                          const newY = Math.max(0, startTop + deltaY);
                          
                          onUpdatePosition(item.id, { 
                            x: Math.round(newX * 2) / 2, // snap to half grid
                            y: Math.round(newY)
                          });
                        };
                        
                        const onMouseUp = () => {
                          document.removeEventListener('mousemove', onMouseMove);
                          document.removeEventListener('mouseup', onMouseUp);
                        };
                        
                        document.addEventListener('mousemove', onMouseMove);
                        document.addEventListener('mouseup', onMouseUp);
                      }}
                    >
                      <Move className="h-4 w-4" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
